import React, { useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { PerformanceMetrics, PlatformPerformanceBreakdown } from '../../types';

type WindowKey = '7d' | '30d' | 'all';

const WINDOW_LABELS: Record<WindowKey, string> = {
  '7d': '7 Days',
  '30d': '30 Days',
  'all': 'All Time',
};

const PLATFORM_LABELS: Record<string, string> = {
  meta: 'Meta',
  youtube: 'YouTube',
  applovin: 'AppLovin',
  total: 'Total',
};

const fmtCurrency = (v: number) =>
  v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(2)}`;

const fmtInt = (v: number) => v.toLocaleString();

const fmtCtr = (clicks: number, impressions: number) =>
  impressions > 0 ? `${((clicks / impressions) * 100).toFixed(2)}%` : '-';

interface ProductionPerformanceProps {
  mode: 'production';
  data: {
    production_performance_7d?: PlatformPerformanceBreakdown;
    production_performance_30d?: PlatformPerformanceBreakdown;
    production_performance_all?: PlatformPerformanceBreakdown;
    production_performance_updated_at?: string;
  };
}

interface AdPerformanceProps {
  mode: 'ad';
  data: {
    performance_7d?: PlatformPerformanceBreakdown;
    performance_30d?: PlatformPerformanceBreakdown;
    performance_all?: PlatformPerformanceBreakdown;
    performance_updated_at?: string;
  };
}

type PerformanceSummaryProps = ProductionPerformanceProps | AdPerformanceProps;

const PerformanceSummary: React.FC<PerformanceSummaryProps> = (props) => {
  const [window, setWindow] = useState<WindowKey>('30d');

  const hasData = props.mode === 'production'
    ? !!(props.data.production_performance_7d || props.data.production_performance_30d || props.data.production_performance_all)
    : !!(props.data.performance_7d || props.data.performance_30d || props.data.performance_all);

  if (!hasData) return null;

  const updatedAt = props.mode === 'production'
    ? props.data.production_performance_updated_at
    : props.data.performance_updated_at;

  if (props.mode === 'production') {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon color="primary" fontSize="small" />
            <Typography variant="h6">Performance</Typography>
          </Box>
          <ToggleButtonGroup
            value={window}
            exclusive
            onChange={(_, v) => v && setWindow(v)}
            size="small"
          >
            {Object.entries(WINDOW_LABELS).map(([k, label]) => (
              <ToggleButton key={k} value={k}>{label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {updatedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Updated {new Date(updatedAt).toLocaleDateString('en-GB')}
          </Typography>
        )}

        <PlatformTable
          data={
            window === '7d' ? props.data.production_performance_7d :
            window === '30d' ? props.data.production_performance_30d :
            props.data.production_performance_all
          }
          level="production"
        />
      </Box>
    );
  }

  // Ad mode — same per-platform breakdown as productions
  const adData =
    window === '7d' ? props.data.performance_7d :
    window === '30d' ? props.data.performance_30d :
    props.data.performance_all;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <TrendingUpIcon color="primary" fontSize="small" />
          <Typography variant="h6">Performance</Typography>
        </Box>
        <ToggleButtonGroup
          value={window}
          exclusive
          onChange={(_, v) => v && setWindow(v)}
          size="small"
        >
          {Object.entries(WINDOW_LABELS).map(([k, label]) => (
            <ToggleButton key={k} value={k}>{label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {updatedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Updated {new Date(updatedAt).toLocaleDateString('en-GB')}
        </Typography>
      )}

      <PlatformTable data={adData} level="ad" />
    </Box>
  );
};

const PlatformTable: React.FC<{ data?: PlatformPerformanceBreakdown; level: 'ad' | 'production' }> = ({ data, level }) => {
  if (!data) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data for this window
      </Typography>
    );
  }

  const rows = Object.keys(data).filter(k => k !== 'total');

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Platform</TableCell>
            <TableCell align="right">Spend</TableCell>
            <TableCell align="right">Impressions</TableCell>
            <TableCell align="right">Clicks</TableCell>
            <TableCell align="right">CTR</TableCell>
            {level === 'ad' && <TableCell align="right">Active Days</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(platform => {
            const m = (data as any)[platform] as PerformanceMetrics | undefined;
            if (!m) return null;
            return (
              <TableRow key={platform}>
                <TableCell>{PLATFORM_LABELS[platform] || platform}</TableCell>
                <TableCell align="right">{fmtCurrency(m.spend)}</TableCell>
                <TableCell align="right">{fmtInt(m.impressions)}</TableCell>
                <TableCell align="right">{fmtInt(m.clicks)}</TableCell>
                <TableCell align="right">{fmtCtr(m.clicks, m.impressions)}</TableCell>
                {level === 'ad' && <TableCell align="right">{m.days_with_spend}</TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PerformanceSummary;
